import { MessageBox } from 'element-ui'

/**
 * 统一确认弹窗
 * ------------------------------------------------------------------
 * 设计定稿：~/.workbuddy/plans/electric-cascade-turing-1DN8USK7.md
 * 视觉规范：src/assets/sass/base.scss 里的 .el-message-box 覆盖块
 *
 * 三条约定：
 * 1. 包装而非替换 Element 的 $confirm —— 签名、Promise 语义、beforeClose 全部透传，
 *    因此全库 63 处 this.$confirm(...) 一行不改即可获得统一样式与标题规范。
 * 2. 不引入图标 —— 不传 type、不传 iconClass，Element 的 status 节点根本不会渲染。
 * 3. 等级只作「行为档」：只有 danger 需要视觉区分（红实心按钮 + 可选后果色块），
 *    warning / normal 视觉相同（同为主色按钮）。
 */

export const SCENE = {
  delete:      { level: 'danger',  title: '删除确认',     confirmText: '删除' },
  batchDelete: { level: 'danger',  title: '批量删除确认', confirmText: '删除' },
  cancelOrder: { level: 'danger',  title: '取消订单确认', confirmText: '取消订单' },
  revoke:      { level: 'danger',  title: '撤销确认',     confirmText: '撤销' },
  logout:      { level: 'danger',  title: '退出登录确认', confirmText: '退出登录' },
  publish:     { level: 'warning', title: '发布确认',     confirmText: '确认发布' },
  unpublish:   { level: 'warning', title: '取消发布确认', confirmText: '确认取消发布' },
  modify:      { level: 'warning', title: '修改确认',     confirmText: '确认修改' },
  rollCall:    { level: 'warning', title: '消课确认',     confirmText: '确认消课' },
  normal:      { level: 'normal',  title: '操作确认',     confirmText: '确定' }
}

const DEFAULT_TITLE = '操作确认'

/**
 * 「XX提示 / XX提醒 / XX警告 / XX成功」→「XX确认」；已带「确认」后缀的不动；无标题给兜底。
 * 一处规则把现状 26 种标题收敛到单一范式：
 *   删除提示→删除确认｜消课提醒→消课确认｜修改成功→修改确认｜退出提示→退出确认
 *   删除班级→删除班级确认｜提示→操作确认｜审核确认→不变
 */
function fixTitle (t) {
  let s = String(t == null ? '' : t).trim()
  if (!s) return DEFAULT_TITLE
  s = s.replace(/(提示|提醒|警告|成功)$/, '').trim()
  if (!s) return DEFAULT_TITLE
  return /确认$/.test(s) ? s : s + '确认'
}

/**
 * 不可逆动作的词表。现状 63 处里绝大多数只传 type:'warning'（51 处），
 * 光看 type 无法识别"删除"这类不可逆操作，必须结合文案。
 */
const DANGER_TEXT = /(删除|移除|撤销|驳回|清空|退出登录|注销|作废|终止|取消订单)/

/** 先剥掉否定表述再判断，避免"关联数据不可删除"这类正文被误判为危险操作 */
const NEGATED = /(不可|不能|无法|不允许|不会)(被)?(删除|移除|撤销|作废)/g

/** 等级只影响行为（关闭策略 / 初始焦点 / 按钮配色），不产生额外视觉装置 */
function pickLevel (opts, dict, text) {
  if (opts.level) return opts.level                 // 调用方显式指定优先
  if (dict && dict.level) return dict.level         // 场景默认（如 unpublish 允许含"取消"字样）
  if (opts.type === 'error') return 'danger'
  if (DANGER_TEXT.test(String(text || '').replace(NEGATED, ''))) return 'danger'
  if (opts.type === 'warning') return 'warning'
  return 'normal'
}

/**
 * Element 在 doOpen 的 nextTick 里会 $refs.confirm.$el.focus()，即回车直接确认。
 * 对删除类操作这是真实误触风险，这里延后把初始焦点纠正到「取消」。
 * （Vue 的 nextTick 是微任务，早于 setTimeout 宏任务，所以本函数一定能覆盖它。）
 */
function focusCancel () {
  const focus = () => {
    const boxes = document.querySelectorAll('.el-message-box')
    const box = boxes[boxes.length - 1]
    if (!box) return false
    const btns = box.querySelectorAll('.el-message-box__btns .el-button')
    if (!btns || !btns[0]) return false
    btns[0].focus()
    return true
  }
  setTimeout(() => {
    if (!focus()) setTimeout(focus, 50)
  }, 0)
}

function normalize (title, options, dict, message) {
  const opts = Object.assign({}, options)
  const level = pickLevel(opts, dict, String(title || '') + ' ' + String(message || ''))
  const typed = String(opts.confirmButtonText == null ? '' : opts.confirmButtonText).trim()

  delete opts.level
  delete opts.type // 不传 type → Element 不渲染状态图标节点（无图标，不是藏图标）

  opts.title = fixTitle(title || (dict && dict.title) || opts.title)
  opts.confirmButtonText = typed || (dict && dict.confirmText) || '确定'
  opts.cancelButtonText = opts.cancelButtonText || '取消'
  opts.customClass = ['sng-confirm', 'sng-confirm--' + level, opts.customClass]
    .filter(Boolean).join(' ')

  if (level === 'danger') {
    const cls = String(opts.confirmButtonClass || '')
    if (cls.indexOf('el-button--danger') < 0) {
      opts.confirmButtonClass = cls ? cls + ' el-button--danger' : 'el-button--danger'
    }
    // 二选一语义下不给「遮罩 / ESC / 右上 ×」三个额外出口，避免误关后要重新勾选
    opts.closeOnClickModal = false
    opts.closeOnPressEscape = false
    opts.showClose = false
  }
  return { opts: opts, level: level }
}

function open (message, opts, level) {
  if (opts.risk) {
    // ⚠️ risk 只允许写代码里的字面量，禁止拼接接口/用户输入（这一段会走 v-html）
    opts.dangerouslyUseHTMLString = true
    message = message + '<p class="sng-confirm__risk">' + opts.risk + '</p>'
    delete opts.risk
  }
  const p = MessageBox.confirm(message, opts.title, opts)
  if (level === 'danger') focusCancel()
  return p
}

/** 签名与 Element 的 this.$confirm(message, title, options) 完全一致 */
export function confirm (message, title, options) {
  const r = normalize(title, options, null, message)
  return open(message, r.opts, r.level)
}

/** 按场景调用：$confirmScene('batchDelete', `确认删除已选的 ${n} 个班级？`) */
export function confirmScene (scene, message, title, options) {
  const r = normalize(title, options, SCENE[scene], message)
  return open(message, r.opts, r.level)
}

export const confirmDelete = (message, extra) => confirmScene('delete', message, null, extra)

export const confirmDanger = (message, title, options) =>
  confirm(message, title, Object.assign({ level: 'danger' }, options))

export function install (Vue) {
  Vue.prototype.$confirm = confirm
  Vue.prototype.$confirmScene = confirmScene
  Vue.prototype.$confirmDelete = confirmDelete
  Vue.prototype.$confirmDanger = confirmDanger
}

export default { install, SCENE, confirm, confirmScene, confirmDelete, confirmDanger }



// WEBPACK FOOTER //
// ./src/assets/js/confirm.js